import { useState, useRef, type ChangeEvent } from 'react';
import { assetUrl } from '../utils/assetUrl'

interface ImageUploadProps {
  onImagesSelected: (files: File[]) => void;
  onImageRemove?: (index: number) => void;
  onSetDefault?: (index: number) => void;
  previewUrls: string[];
  defaultIndex?: number;
  uploadProgress?: number[];
  maxImages?: number;
}

export default function ImageUpload({
  onImagesSelected,
  onImageRemove,
  onSetDefault,
  previewUrls,
  defaultIndex = 0,
  uploadProgress = [],
  maxImages = 8
}: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const styles: React.CSSProperties = {
    '--text-secondary': '#4A5568',
    '--slate-light': '#718096',
    '--border': '#E2E8F0',
    '--bg-primary': '#F7FAF7',
    '--forest-green': '#1B5E20',
  } as React.CSSProperties;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onImagesSelected(files);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      onImagesSelected(files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const canAddMore = previewUrls.length < maxImages;

  return (
    <div style={styles}>
      <label style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.75rem'}}>
        Photos
      </label>

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem'}} className="sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {/* Existing previews */}
        {previewUrls.map((url, index) => (
          <div
            key={index}
            style={{
              position: 'relative',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '2px solid var(--border)',
              aspectRatio: '1'
            }}
          >
            <img
              src={assetUrl(url)}
              alt={`Preview ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            {uploadProgress[index] !== undefined && uploadProgress[index] < 100 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor: 'rgba(0,0,0,0.1)'
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${uploadProgress[index]}%`,
                    backgroundColor: 'var(--forest-green)',
                    transition: 'width 0.2s'
                  }}
                />
              </div>
            )}
            {onImageRemove && (
              <button
                type="button"
                onClick={() => onImageRemove(index)}
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'rgba(220, 38, 38, 0.9)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  zIndex: 2
                }}
              >
                ×
              </button>
            )}

            {onSetDefault && (
              <button
                type="button"
                onClick={() => onSetDefault(index)}
                style={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '4px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: index === defaultIndex ? 'var(--forest-green)' : 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  zIndex: 2
                }}
              >
                {index === defaultIndex ? 'Default' : 'Set default'}
              </button>
            )}

            {index === defaultIndex && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: '3px solid var(--forest-green)',
                  borderRadius: '8px',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        ))}

        {/* Add button */}
        {canAddMore && (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: `2px dashed ${isDragging ? 'var(--forest-green)' : 'var(--border)'}`,
              borderRadius: '8px',
              aspectRatio: '1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: isDragging ? 'rgba(27, 94, 32, 0.05)' : 'var(--bg-primary)',
              transition: 'all 0.2s'
            }}
          >
            <span style={{fontSize: '1.5rem', color: 'var(--slate-light)', marginBottom: '4px'}}>+</span>
            <span style={{fontSize: '0.75rem', color: 'var(--slate-light)', textAlign: 'center', padding: '0 8px'}}>
              {isDragging ? 'Drop here' : 'Add Photo'}
            </span>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/webp,image/png,image/gif,image/heic,image/heif,image/avif,.jpg,.jpeg,.webp,.png,.gif,.heic,.heif,.avif"
        multiple
        onChange={handleFileChange}
        style={{display: 'none'}}
      />

      <p style={{fontSize: '0.75rem', color: 'var(--slate-light)', marginTop: '0.5rem', marginBottom: 0}}>
        Tap to choose photos or drag & drop. Max {maxImages} images. JPEG, PNG, WEBP, GIF, HEIC/HEIF, or AVIF. Each image must be under 10 MB.
      </p>
    </div>
  );
}
